import React, { useEffect, useState } from 'react';
import { FaSyncAlt, FaCheck, FaTimes } from 'react-icons/fa';
import './index.css';
import { usePlatforms } from '../../hooks/usePlatforms';
import { usePackages } from '../../hooks/usePackages';
import { useClient } from '../../hooks/useClient';
import { translate } from '../../utils/Common';
import buttonStyles from '../common/Button/Button.module.css';

const BtnSync = (props) => {
  const { mode, platformId, settingId, boardId, doorId, locationId, packageId, reservationId, clientId, companyCode, assignmentId, label, size='md', variant = 'btn', id='', setLoading } = props;
  const { syncLocationToLocker, syncSettingsToLocker, syncModuleToLocker, syncDoorToLocker, forceSynchronization, syncCompanyToLocker, syncAssignmentToLocker } = usePlatforms();
  const {syncPackageToLocker} = usePackages();
  const { syncClientToLocker, syncClientsByPlatform } = useClient();
  // Texto y tooltip: `label` permite distinguir dos botones de sync que conviven (p. ej. el sync
  // completo del locker y el de solo sus clientes), que si no quedarían idénticos.
  const syncLabel = label ?? translate('global.sync');
  const [status, setStatus] = useState('idle'); // 'idle', 'success', 'error', 'loading'
  const [timer, setTimer] = useState(0);

  const handleClick = async () => {
    setStatus('loading');
    setTimer(0);

    try {
      setLoading && setLoading(true);
      const response = await handleSync();
      
      if (response?.code === 200) {
        setStatus('success'); // Muestra el check verde
      } else {
        setStatus('error'); // Muestra la X roja
      }

      setTimeout(() => {
        setLoading && setLoading(false);
      }, 3000);

    } catch (error) {
      console.error("Error durante la sincronización:", error);
      setStatus('error'); // En caso de error, también muestra la X roja
    } finally {
      // Vuelve al estado inicial después de 3 segundos
      setTimeout(() => {
        setStatus('idle');
        setTimer(0);
        setLoading && setLoading(false);
      }, 3000);
    }
  };

  const handleSync = async () => {
    switch (mode) {
      case "general":
        return syncLocationToLocker({ platformId });
      case "settings":
        return syncSettingsToLocker({ settingId });
      case "module":
        return syncModuleToLocker({ boardId });
      case "door":
        return syncDoorToLocker({ doorId });
        case "force":
          return forceSynchronization({ locationId });
        case "package":
          return syncPackageToLocker({ packageId, reservationId });
        case "client":
          return syncClientToLocker({ clientId });
        case "company":
          return syncCompanyToLocker({ companyCode });
        case "assignment":
          return syncAssignmentToLocker({ assignmentId });
        case "locker-clients":
          return syncClientsByPlatform({ platformId });

      default:
        console.warn(`Modo no reconocido: ${mode}`);
        return Promise.resolve();
    }
  };

  const renderIcon = () => {
    switch (status) {
      case 'loading':
        return <FaSyncAlt className={`icon spinning ${size}`} />;
      case 'success':
        return <FaCheck className={`icon success ${size}`} />;
      case 'error':
        return <FaTimes className={`icon error ${size}`} />;
      default:
        return <FaSyncAlt className={`icon ${size}`} />;
    }
  };

  const renderOutlineIcon = () => {
    const iconSize = 14;
    switch (status) {
      case 'success':
        return <FaCheck size={iconSize} style={{ color: 'var(--color-success, #34C759)' }} />;
      case 'error':
        return <FaTimes size={iconSize} style={{ color: 'var(--color-error, #FF3B30)' }} />;
      default:
        return <FaSyncAlt size={iconSize} />;
    }
  };

   // Efecto para manejar el temporizador
   useEffect(() => {
    let interval = null;

    if (status === 'loading' && variant === 'timer') {
      interval = setInterval(() => {
        setTimer((prev) => prev + 1);
      }, 1000); // Incrementa cada segundo
    } else {
      clearInterval(interval);
    }

    return () => clearInterval(interval);
  }, [status, variant]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`; // Formato MM:SS
  };

  if (variant === 'outline') {
    const outlineClasses = [
      buttonStyles.button,
      buttonStyles.outline,
      buttonStyles.sm,
      status === 'loading' && buttonStyles.loading,
    ].filter(Boolean).join(' ');
    return (
      <button
        type="button"
        className={outlineClasses}
        onClick={handleClick}
        disabled={status === 'loading'}
        title={syncLabel}
        id={id}
      >
        {status === 'loading' ? (
          <span className={buttonStyles.spinner}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle className={buttonStyles.spinnerCircle} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className={buttonStyles.spinnerPath} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </span>
        ) : (
          <span className={buttonStyles.leftIcon}>{renderOutlineIcon()}</span>
        )}
        <span className={buttonStyles.content}>{syncLabel}</span>
      </button>
    );
  }

  return (
    <div
      className={`btn-sync ${size} ${status} ${variant}`}
      onClick={handleClick}
      title={syncLabel}
      id={id}
    >
      {variant === 'btn' && renderIcon()}
      {variant === 'timer' && (
        <div className="timer-wrapper">
          <FaSyncAlt className={`icon success ${size}`} />
          <span className="timer-text">{formatTime(timer)}</span>
        </div>
      )}
    </div>
  );
};

export default BtnSync;
